import React, { useState } from 'react';
import KaModal from '../Common/KaModal';
import { openReportInNewTab } from './openReportInNewTab';
import KaAlert from '../Common/KaAlert';
import SpinnerButton from '../Common/SpinnerButton';
import useDisplayMessage from '../Common/useDisplayMessage';
import useGetTransportHistoryReport from '../../requests/useGetTransportHistoryReport';
import { TransportHistoryReportDisplay, reportColumns } from './TransportHistoryReportDisplay';
import { KaSelect, toKaSelectOptions, isMultiValue, KaSelectOption } from '../Common/KaSelect';
import { unitList } from '../Common/Units';
import { Row, Col, Label } from 'reactstrap';
import useGetSitesNonAdmin from '../Requests/useGetSitesNonAdmin';
import MultiQueryView from '../Common/MultiQueryView';
import useGetTransports from '../../requests/useGetTransports';
import dayjs, { Dayjs } from 'dayjs';
import DateRange from '../Common/DateRange';

interface TransportHistoryReportModalProps {
  show: boolean;
  onHide: () => void;
}

export const TransportHistoryReportModal = ({ show, onHide }: TransportHistoryReportModalProps) => {
  const displayMessage = useDisplayMessage();
  const columnOptions = reportColumns.map(rc => ({ value: rc, label: rc }));

  const [selectedUnit, setSelectedUnit] = useState("Pound");
  const [selectedTransport, setSelectedTransport] = useState<string>("");
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<KaSelectOption[]>(columnOptions);

  const dateFormat = 'MM/DD/YYYY';
  const toMydString = (date: Dayjs): string => date.format(dateFormat);
  const [dateFilterChecked, setDateFilterChecked] = useState(true);
  const [selectedDates, setSelectedDates] = useState({
    startDate: toMydString(dayjs().subtract(1, 'month')),
    endDate: toMydString(dayjs())
  });

  const getSites = useGetSitesNonAdmin();
  const getTransports = useGetTransports();

  const queries = {
    sites: getSites,
    transports: getTransports,
  };

  const anyQueriesLoading = getSites.isLoading || getTransports.isLoading;

  const handleClose = () => {
    onHide();
    displayMessage.clear();
    setSelectedUnit("Pound");
    setSelectedTransport("");
    setSelectedSite("");
  };

  const params = {
    units: selectedUnit,
    transportId: selectedTransport || undefined,
    siteId: selectedSite || undefined,
    startDate: dateFilterChecked ? selectedDates.startDate : undefined,
    endDate: dateFilterChecked ? selectedDates.endDate : undefined,
  };

  const reportQuery = useGetTransportHistoryReport(
    params,
    {
      onSuccess: (reportData) => {
        openReportInNewTab(
          'Transport History Report',
          <TransportHistoryReportDisplay reportData={reportData} columns={selectedColumns.map(c => c.value)} />,
          displayMessage.fail,
          handleClose
        );
      },
      onError: (error) => {
        displayMessage.fail(error.message || 'Failed to generate report');
      }
    }
  );

  const unitOptions = toKaSelectOptions(unitList(), "value", "name", { sortByLabel: true });

  const handleGenerateReport = () => {
    displayMessage.clear();
    reportQuery.query();
  };

  return (
    <KaModal
      show={show}
      onHide={handleClose}
      title="Transport History Report"
      body={
        <>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <p>
            Generate a detailed report showing which customer orders and product mixes were loaded into each transport compartment, including quantities, product measurements, and associated operational details.
          </p>
          <MultiQueryView
            queries={queries}
            renderData={_ => <>
              <Row className="mb-5">
                <Col md={6}>
                  <Label htmlFor="unit-selector" className="form-label">
                    Display Units
                  </Label>
                  <KaSelect
                    inputId="unit-selector"
                    options={unitOptions}
                    value={unitOptions.find(option => option.value === selectedUnit)}
                    onChange={(option) => setSelectedUnit(isMultiValue(option) ? "Pound" : (option?.value || "Pound"))}
                    placeholder="Select unit..."
                    isSearchable={false}
                  />
                </Col>
              </Row>
              <h5 className="mb-2">
                Show Columns
              </h5>
              <Row className="mb-5">
                <Col>
                  <KaSelect
                    placeholder="Select Columns"
                    options={columnOptions}
                    isClearable={true}
                    closeMenuOnSelect={false}
                    isMulti={true}
                    onChange={(valueObject) => {
                      if (isMultiValue(valueObject)) { setSelectedColumns(valueObject.map(c => c as KaSelectOption)); }
                    }}
                    value={selectedColumns} />
                </Col>
              </Row>
              <h5 className="mb-2">
                Filter report
              </h5>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="transport-selector" className="form-label">
                    Transport:
                  </Label>
                  <KaSelect
                    inputId="transport-selector"
                    options={toKaSelectOptions(getTransports.data ?? [], "id", "name", { sortByLabel: true })}
                    value={toKaSelectOptions(getTransports.data ?? [], "id", "name", { sortByLabel: true }).find(option => option.value === selectedTransport)}
                    onChange={(option) => setSelectedTransport(isMultiValue(option) ? "" : (option?.value || ""))}
                    placeholder="Select transport"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="site-selector" className="form-label">
                    Site:
                  </Label>
                  <KaSelect
                    inputId="site-selector"
                    options={toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true })}
                    value={toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true }).find(option => option.value === selectedSite)}
                    onChange={(option) => setSelectedSite(isMultiValue(option) ? "" : (option?.value || ""))}
                    placeholder="Select site"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col className="form-check ms-3">
                  <input
                    type="checkbox"
                    id="toggleDateFilter"
                    className="form-check-input"
                    checked={dateFilterChecked}
                    onChange={(e) => setDateFilterChecked(e.target.checked)} />
                  <label htmlFor="toggleDateFilter" className="form-check-label">
                    Filter by date range
                  </label>
                </Col>
              </Row>
              {dateFilterChecked && (
                <Row>
                  <Col>
                    <DateRange
                      className="row mt-3"
                      startDate={selectedDates.startDate}
                      endDate={selectedDates.endDate}
                      rangeUpdated={(startDate, endDate) => setSelectedDates({ startDate, endDate })}
                      dateFormat={dateFormat} />
                  </Col>
                </Row>
              )}
            </>} />
        </>
      }
      footer={!anyQueriesLoading && <>
        <SpinnerButton
          spinning={reportQuery.isLoading}
          onClick={handleGenerateReport}>
          View in New Tab
        </SpinnerButton>
      </>
      }
    />
  );
};
